// src/components/SignatureModal.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { auth, db, storage, firebaseConfig } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Configure PDF.js worker for inline canvas rendering (prevents browser auto-downloads)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SIGNATURE_FONTS = [
    {
        id: 'script',
        label: 'Classic Script',
        fontFamily: '"Dancing Script", "Brush Script MT", "Segoe Script", cursive',
        fontSize: 42
    },
    {
        id: 'executive',
        label: 'Executive Cursive',
        fontFamily: '"Great Vibes", "Snell Roundhand", "Apple Chancery", cursive',
        fontSize: 44
    },
    {
        id: 'formal',
        label: 'Formal Italic',
        fontFamily: '"Sacramento", "Lucida Handwriting", "Georgia", serif',
        fontSize: 40,
        italic: true
    }
];

// Renders a cursive signature or initials string onto an HTML5 canvas and returns a transparent PNG Base64 data URL
const renderTextToSignaturePng = (text, styleObj, isInitials = false) => {
    const canvas = document.createElement('canvas');
    canvas.width = isInitials ? 240 : 560;
    canvas.height = 140;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fontStyle = styleObj.italic ? 'italic ' : '';
    const size = isInitials ? 46 : styleObj.fontSize || 42;
    ctx.font = `${fontStyle}600 ${size}px ${styleObj.fontFamily}`;
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const cleanText = (text || 'Client Signature').trim();
    ctx.fillText(cleanText, canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL('image/png');
};

const SignatureModal = ({
    clientId,
    requestId,
    documentName,
    documentUrl: propDocUrl,
    clientData,
    onSuccess,
    onCancel
}) => {
    const [activeTab, setActiveTab] = useState('read'); // 'read', 'draw', 'upload'
    const [resolvedDocUrl, setResolvedDocUrl] = useState(propDocUrl || '');
    const [requestRecord, setRequestRecord] = useState(null);
    const [requiredFields, setRequiredFields] = useState([]);
    const [customFieldValues, setCustomFieldValues] = useState({});

    // Inline PDF Viewer State
    const [numPages, setNumPages] = useState(null);
    const [pdfScale, setPdfScale] = useState(0.9);
    const [openingBlobTab, setOpeningBlobTab] = useState(false);

    // Signature Mode: 'adopt' (1-click cursive signature) vs 'draw' (freehand scribble)
    const [signatureMode, setSignatureMode] = useState('adopt');
    const [selectedFontId, setSelectedFontId] = useState('script');
    const [adoptedName, setAdoptedName] = useState('');
    const [adoptedInitials, setAdoptedInitials] = useState('');

    // Physical Upload State
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const sigPadRefs = useRef({});

    // 1. Resolve Client Name, Email, Initials, and Today's Date automatically from CRM clientData
    const autoClientInfo = useMemo(() => {
        const signerFromReq = requestRecord?.signers?.[0] || {};

        let fullName = '';
        if (clientData?.firstName || clientData?.lastName) {
            fullName = `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim();
        } else if (clientData?.clientType === 'Corporate') {
            const officer = clientData.signingOfficer
                ? `${clientData.signingOfficer.firstName || ''} ${clientData.signingOfficer.lastName || ''}`.trim()
                : '';
            fullName = officer || clientData.legalName || clientData.name || '';
        } else if (clientData?.clientType === 'Trust') {
            fullName = clientData.trustName || clientData.name || '';
        } else if (clientData?.clientType === 'Partnership') {
            fullName = clientData.partnershipName || clientData.name || '';
        } else {
            fullName = clientData?.name || clientData?.legalName || '';
        }

        if (!fullName) {
            fullName =
                requestRecord?.recipientName ||
                signerFromReq.name ||
                auth.currentUser?.displayName ||
                'Client';
        }

        const email =
            clientData?.email ||
            clientData?.corpEmail ||
            clientData?.trustEmail ||
            clientData?.signingOfficer?.email ||
            requestRecord?.recipientEmail ||
            signerFromReq.email ||
            auth.currentUser?.email ||
            '';

        const nameParts = fullName.split(/\s+/).filter(Boolean);
        const initials =
            nameParts.length >= 2
                ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
                : fullName.slice(0, 2).toUpperCase();

        const todayDate = new Date().toISOString().split('T')[0];

        return {
            name: fullName,
            email,
            initials,
            date: todayDate
        };
    }, [clientData, requestRecord]);

    useEffect(() => {
        if (autoClientInfo.name && !adoptedName) {
            setAdoptedName(autoClientInfo.name);
        }
        if (autoClientInfo.initials && !adoptedInitials) {
            setAdoptedInitials(autoClientInfo.initials);
        }
    }, [autoClientInfo, adoptedName, adoptedInitials]);

    // 2. Load the Signature Request directly from Firestore to get the PDF URL and placed fields
    useEffect(() => {
        const fetchRequestDetails = async () => {
            if (!clientId || !requestId) return;
            try {
                const reqRef = doc(db, 'clients', clientId, 'signatureRequests', requestId);
                const snap = await getDoc(reqRef);
                if (snap.exists()) {
                    const data = snap.data();
                    setRequestRecord(data);

                    const dbUrl =
                        data.documentUrl ||
                        data.fileUrl ||
                        data.originalFileUrl ||
                        data.path ||
                        data.url ||
                        propDocUrl ||
                        '';
                    if (dbUrl) setResolvedDocUrl(dbUrl);

                    if (Array.isArray(data.fields) && data.fields.length > 0) {
                        setRequiredFields(data.fields);
                    } else if (data.signatureSetup) {
                        setRequiredFields([
                            {
                                id: 'legacy_sig',
                                type: 'signature',
                                pageNumber: data.signatureSetup.pageNumber || 1,
                                xPercent: data.signatureSetup.xPercent ?? 20,
                                yPercent: data.signatureSetup.yPercent ?? 70
                            }
                        ]);
                    } else {
                        setRequiredFields([
                            {
                                id: 'default_sig',
                                type: 'signature',
                                pageNumber: 1,
                                xPercent: 20,
                                yPercent: 75
                            }
                        ]);
                    }
                }
            } catch (err) {
                console.error('Error loading signature request details:', err);
            }
        };

        fetchRequestDetails();
    }, [clientId, requestId, propDocUrl]);

    // Opens the PDF inline in a new browser tab using a local Blob URL so it views/prints instead of auto-downloading
    const handleOpenInlineNewTab = async () => {
        if (!resolvedDocUrl) return;
        setOpeningBlobTab(true);
        try {
            const response = await fetch(resolvedDocUrl);
            const arrayBuffer = await response.arrayBuffer();
            const pdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(pdfBlob);
            window.open(blobUrl, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (err) {
            window.open(resolvedDocUrl, '_blank', 'noopener,noreferrer');
        } finally {
            setOpeningBlobTab(false);
        }
    };

    // Normalize field coordinates so the Cloud Function always receives pageNumber, xPercent, and yPercent
    const normalizeFieldCoords = (field) => {
        const pageNumber = Number(field.pageNumber || field.page || 1);
        const xPercent =
            field.xPercent !== undefined
                ? Number(field.xPercent)
                : field.x !== undefined
                    ? Number(((Number(field.x) / 612) * 100).toFixed(2))
                    : 20;
        const yPercent =
            field.yPercent !== undefined
                ? Number(field.yPercent)
                : field.y !== undefined
                    ? Number(((Number(field.y) / 792) * 100).toFixed(2))
                    : 70;
        return { pageNumber, xPercent, yPercent };
    };

    const isSignatureField = (type) => type === 'signature' || type === 'signature_block';
    const isInitialsField = (type) => type === 'initials';
    const isAutoNameField = (type) => type === 'recipient_name' || type === 'name';
    const isAutoEmailField = (type) => type === 'recipient_email' || type === 'email';
    const isAutoDateField = (type) => type === 'signing_date' || type === 'date';

    // Only custom text/number fields require manual input; Name, Email, Date, and Initials are automatic
    const manualInputFields = useMemo(() => {
        return requiredFields.filter(
            (f) =>
                !isSignatureField(f.type) &&
                !isInitialsField(f.type) &&
                !isAutoNameField(f.type) &&
                !isAutoEmailField(f.type) &&
                !isAutoDateField(f.type)
        );
    }, [requiredFields]);

    const signatureFields = useMemo(() => {
        const sigs = requiredFields.filter((f) => isSignatureField(f.type));
        return sigs.length > 0
            ? sigs
            : [{ id: 'primary_sig', type: 'signature', pageNumber: 1, xPercent: 20, yPercent: 75 }];
    }, [requiredFields]);

    const activeFontStyle = useMemo(() => {
        return SIGNATURE_FONTS.find((f) => f.id === selectedFontId) || SIGNATURE_FONTS[0];
    }, [selectedFontId]);

    const clearSinglePad = (id) => {
        if (sigPadRefs.current[id]) {
            sigPadRefs.current[id].clear();
        }
    };

    // --- SUBMIT HANDLER ---
    const handleSubmit = async () => {
        if (!auth.currentUser) {
            alert('You must be signed in to submit this document.');
            return;
        }

        setIsSubmitting(true);
        try {
            const idToken = await auth.currentUser.getIdToken();

            const payload = {
                idToken,
                clientId,
                requestId,
                documentUrl: resolvedDocUrl,
                uploadedFileUrl: null,
                signatureBase64: null,
                fields: []
            };

            if (activeTab === 'upload') {
                if (!selectedFile) {
                    alert('Please select your signed file before submitting.');
                    setIsSubmitting(false);
                    return;
                }
                const currentYear = new Date().getFullYear();
                const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                const storagePath = `clients/${clientId}/Documents/${currentYear}/Signed_${Date.now()}_${safeName}`;
                const storageRef = ref(storage, storagePath);

                const snap = await uploadBytes(storageRef, selectedFile);
                payload.uploadedFileUrl = await getDownloadURL(snap.ref);
            } else {
                // Digital Signature Route (Supports both 'adopt' and 'draw')
                let primarySignaturePng = null;

                if (signatureMode === 'adopt') {
                    const nameToSign = (adoptedName || autoClientInfo.name || 'Client').trim();
                    if (!nameToSign) {
                        alert('Please enter your name to adopt a signature.');
                        setIsSubmitting(false);
                        return;
                    }
                    primarySignaturePng = renderTextToSignaturePng(nameToSign, activeFontStyle, false);
                } else {
                    for (const sigField of signatureFields) {
                        const pad = sigPadRefs.current[sigField.id];
                        if (!pad || pad.isEmpty()) {
                            alert('Please draw your signature in the signature box (or switch to "Adopt Signature").');
                            setIsSubmitting(false);
                            return;
                        }
                    }
                    const firstPad = sigPadRefs.current[signatureFields[0].id];
                    primarySignaturePng = firstPad.getCanvas().toDataURL('image/png');
                }

                payload.signatureBase64 = primarySignaturePng;

                // Build the completed fields array with auto-filled CRM data
                const builtFields = requiredFields.map((field) => {
                    const coords = normalizeFieldCoords(field);

                    if (isSignatureField(field.type)) {
                        const fieldSigPng =
                            signatureMode === 'adopt'
                                ? primarySignaturePng
                                : sigPadRefs.current[field.id]?.getCanvas().toDataURL('image/png') ||
                                primarySignaturePng;
                        return {
                            ...field,
                            ...coords,
                            type: 'signature',
                            value: fieldSigPng
                        };
                    }

                    if (isInitialsField(field.type)) {
                        const initialsText = (adoptedInitials || autoClientInfo.initials || 'CS').trim();
                        return {
                            ...field,
                            ...coords,
                            type: 'name',
                            value: initialsText
                        };
                    }

                    if (isAutoNameField(field.type)) {
                        return {
                            ...field,
                            ...coords,
                            type: 'name',
                            value: (adoptedName || autoClientInfo.name).trim()
                        };
                    }

                    if (isAutoEmailField(field.type)) {
                        return {
                            ...field,
                            ...coords,
                            type: 'name',
                            value: autoClientInfo.email
                        };
                    }

                    if (isAutoDateField(field.type)) {
                        return {
                            ...field,
                            ...coords,
                            type: 'date',
                            value: autoClientInfo.date
                        };
                    }

                    return {
                        ...field,
                        ...coords,
                        type: 'name',
                        value: customFieldValues[field.id] || ''
                    };
                });

                payload.fields = builtFields;
            }

            const activeProjectId = firebaseConfig?.projectId || 'nvs-app-developer';
            const functionUrl = `https://us-central1-${activeProjectId}.cloudfunctions.net/submitSignature`;
            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (!response.ok || result.error) {
                throw new Error(result.error || 'Failed to submit signature.');
            }

            alert('Document signed and submitted successfully!');
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Submission Error:', error);
            alert(`Error: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            {/* Load Google cursive fonts for signature adoption */}
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&family=Great+Vibes&family=Sacramento&display=swap"
            />

            <div className="bg-[#1e2530] text-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh] border border-gray-700">
                {/* Header */}
                <div className="p-5 border-b border-gray-700/80 flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold text-white">Review & Sign Document</h3>
                        <p className="text-sm text-gray-400 mt-1">
                            Document: <span className="font-semibold text-blue-400">{documentName}</span>
                        </p>
                    </div>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-white text-lg font-bold px-2"
                    >
                        ✕
                    </button>
                </div>

                {/* Step Navigation Tabs */}
                <div className="flex border-b border-gray-700/80 bg-[#171d26]">
                    <button
                        onClick={() => setActiveTab('read')}
                        className={`flex-1 py-3.5 text-sm font-semibold text-center border-b-2 transition-colors ${activeTab === 'read'
                            ? 'border-blue-500 text-blue-400 bg-[#1e2530]'
                            : 'border-transparent text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        1. Read Document
                    </button>
                    <button
                        onClick={() => setActiveTab('draw')}
                        className={`flex-1 py-3.5 text-sm font-semibold text-center border-b-2 transition-colors ${activeTab === 'draw'
                            ? 'border-blue-500 text-blue-400 bg-[#1e2530]'
                            : 'border-transparent text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        2. Complete Fields
                    </button>
                    <button
                        onClick={() => setActiveTab('upload')}
                        className={`flex-1 py-3.5 text-sm font-semibold text-center border-b-2 transition-colors ${activeTab === 'upload'
                            ? 'border-blue-500 text-blue-400 bg-[#1e2530]'
                            : 'border-transparent text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        3. Print & Upload
                    </button>
                </div>

                {/* Body Content */}
                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    {/* ==========================================
                        TAB 1: INLINE CANVAS PDF PREVIEW (NO AUTO-DOWNLOAD)
                       ========================================== */}
                    {activeTab === 'read' && (
                        <div className="flex flex-col h-full space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-xs text-gray-400">
                                    Please review the contents of the document below before proceeding to sign.
                                </p>

                                {resolvedDocUrl && (
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center bg-[#171d26] border border-gray-700 rounded-lg px-2 py-1 text-xs">
                                            <button
                                                type="button"
                                                onClick={() => setPdfScale((s) => Math.max(0.5, Number((s - 0.15).toFixed(2))))}
                                                className="px-2 font-bold text-gray-300 hover:text-white"
                                                title="Zoom Out"
                                            >
                                                -
                                            </button>
                                            <span className="px-2 text-gray-400 font-mono">
                                                {Math.round(pdfScale * 100)}%
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setPdfScale((s) => Math.min(1.6, Number((s + 0.15).toFixed(2))))}
                                                className="px-2 font-bold text-gray-300 hover:text-white"
                                                title="Zoom In"
                                            >
                                                +
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleOpenInlineNewTab}
                                            disabled={openingBlobTab}
                                            className="text-xs font-semibold text-blue-400 hover:underline flex items-center gap-1"
                                        >
                                            {openingBlobTab ? 'Opening...' : '↗ Open Full PDF in New Tab'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {resolvedDocUrl ? (
                                <div className="w-full h-[420px] bg-[#11161d] rounded-xl overflow-y-auto border border-gray-700 p-4 flex flex-col items-center">
                                    <Document
                                        file={resolvedDocUrl}
                                        onLoadSuccess={({ numPages: total }) => setNumPages(total)}
                                        loading={
                                            <div className="py-20 text-sm text-gray-400 font-medium">
                                                Loading PDF preview...
                                            </div>
                                        }
                                        error={
                                            <div className="py-16 text-center text-sm text-gray-400 space-y-3">
                                                <p>Unable to render inline preview.</p>
                                                <button
                                                    type="button"
                                                    onClick={handleOpenInlineNewTab}
                                                    className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg"
                                                >
                                                    View PDF in New Tab
                                                </button>
                                            </div>
                                        }
                                    >
                                        {Array.from(new Array(numPages || 0), (_, index) => {
                                            const pageNum = index + 1;
                                            const fieldsOnPage = requiredFields.filter(
                                                (f) => Number(f.pageNumber || f.page || 1) === pageNum
                                            );
                                            return (
                                                <div
                                                    key={`preview_page_${pageNum}`}
                                                    className="relative bg-white shadow-lg rounded overflow-hidden mb-6 last:mb-0"
                                                >
                                                    <Page
                                                        pageNumber={pageNum}
                                                        scale={pdfScale}
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={false}
                                                    />
                                                    {/* Visual markers showing where the client's signature/info will be stamped */}
                                                    {fieldsOnPage.map((f) => {
                                                        const coords = normalizeFieldCoords(f);
                                                        return (
                                                            <div
                                                                key={f.id}
                                                                onClick={() => setActiveTab('draw')}
                                                                title="Click to complete & sign"
                                                                className="absolute border-2 border-blue-600 bg-blue-500/20 text-blue-900 rounded px-1.5 py-0.5 text-[10px] font-bold flex items-center cursor-pointer hover:bg-blue-500/30 transition-colors"
                                                                style={{
                                                                    left: `${coords.xPercent}%`,
                                                                    top: `${coords.yPercent}%`,
                                                                    minWidth: '80px',
                                                                    minHeight: '24px'
                                                                }}
                                                            >
                                                                ✍️ {f.label || f.type}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </Document>
                                </div>
                            ) : (
                                <div className="w-full h-64 flex flex-col items-center justify-center border border-dashed border-gray-600 rounded-xl text-gray-400 p-6 text-center">
                                    <p className="font-semibold text-gray-300 mb-1">
                                        Document preview unavailable.
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={() => setActiveTab('draw')}
                                className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow transition-colors text-sm"
                            >
                                Proceed to Sign &rarr;
                            </button>
                        </div>
                    )}

                    {/* ==========================================
                        TAB 2: COMPLETE FIELDS (AUTO-FILLED + ADOPT OR DRAW SIGNATURE)
                       ========================================== */}
                    {activeTab === 'draw' && (
                        <div className="space-y-6">
                            {/* Auto-Populated Client Details Banner (Pulled automatically from CRM) */}
                            <div className="p-4 rounded-xl border border-gray-700 bg-[#252d3a]">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-400">
                                        ✓ Auto-Filled from Your Client Profile
                                    </span>
                                    <span className="text-[11px] text-gray-400">
                                        Stamped automatically upon signing
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                                    <div className="bg-[#1e2530] p-2.5 rounded-lg border border-gray-700">
                                        <span className="text-gray-400 block text-[10px] uppercase font-bold">
                                            Recipient Name
                                        </span>
                                        <span className="text-white font-semibold truncate block mt-0.5">
                                            {adoptedName || autoClientInfo.name}
                                        </span>
                                    </div>
                                    <div className="bg-[#1e2530] p-2.5 rounded-lg border border-gray-700">
                                        <span className="text-gray-400 block text-[10px] uppercase font-bold">
                                            Recipient Email
                                        </span>
                                        <span className="text-white font-semibold truncate block mt-0.5">
                                            {autoClientInfo.email || 'Verified via Portal'}
                                        </span>
                                    </div>
                                    <div className="bg-[#1e2530] p-2.5 rounded-lg border border-gray-700">
                                        <span className="text-gray-400 block text-[10px] uppercase font-bold">
                                            Signing Date & Initials
                                        </span>
                                        <span className="text-white font-semibold block mt-0.5">
                                            {autoClientInfo.date} &bull; Initials: {adoptedInitials || autoClientInfo.initials}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Signature Required Panel with Adopt vs. Draw Toggle */}
                            <div className="p-5 rounded-xl border border-gray-700 bg-[#252d3a] space-y-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <label className="text-sm font-bold text-white">
                                        Signature Required
                                    </label>
                                    <div className="inline-flex rounded-lg bg-[#171d26] p-1 border border-gray-700">
                                        <button
                                            type="button"
                                            onClick={() => setSignatureMode('adopt')}
                                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${signatureMode === 'adopt'
                                                ? 'bg-blue-600 text-white shadow'
                                                : 'text-gray-400 hover:text-white'
                                                }`}
                                        >
                                            ✨ Adopt Signature
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSignatureMode('draw')}
                                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${signatureMode === 'draw'
                                                ? 'bg-blue-600 text-white shadow'
                                                : 'text-gray-400 hover:text-white'
                                                }`}
                                        >
                                            ✍️ Draw Signature
                                        </button>
                                    </div>
                                </div>

                                {signatureMode === 'adopt' ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <div className="sm:col-span-2">
                                                <label className="block text-[11px] font-semibold text-gray-400 mb-1">
                                                    Full Name (From CRM)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={adoptedName}
                                                    onChange={(e) => setAdoptedName(e.target.value)}
                                                    className="w-full px-3 py-2 rounded-lg bg-[#1e2530] border border-gray-600 text-white text-sm focus:outline-none focus:border-blue-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-semibold text-gray-400 mb-1">
                                                    Initials
                                                </label>
                                                <input
                                                    type="text"
                                                    value={adoptedInitials}
                                                    onChange={(e) => setAdoptedInitials(e.target.value)}
                                                    className="w-full px-3 py-2 rounded-lg bg-[#1e2530] border border-gray-600 text-white text-sm focus:outline-none focus:border-blue-500"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[11px] font-semibold text-gray-400 mb-2">
                                                Select Signature Style to Adopt:
                                            </label>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                {SIGNATURE_FONTS.map((font) => {
                                                    const isSelected = selectedFontId === font.id;
                                                    return (
                                                        <button
                                                            key={font.id}
                                                            type="button"
                                                            onClick={() => setSelectedFontId(font.id)}
                                                            className={`p-3 rounded-xl border text-center transition-all bg-white text-slate-900 ${isSelected
                                                                ? 'border-blue-500 ring-2 ring-blue-500 shadow-lg'
                                                                : 'border-gray-300 opacity-80 hover:opacity-100'
                                                                }`}
                                                        >
                                                            <div
                                                                className="text-2xl truncate py-2"
                                                                style={{
                                                                    fontFamily: font.fontFamily,
                                                                    fontStyle: font.italic ? 'italic' : 'normal'
                                                                }}
                                                            >
                                                                {adoptedName || autoClientInfo.name || 'Signature'}
                                                            </div>
                                                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 border-t border-gray-100 pt-1 mt-1">
                                                                {font.label} {isSelected ? '✓' : ''}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {signatureFields.map((field, index) => (
                                            <div key={field.id} className="space-y-1.5">
                                                {signatureFields.length > 1 && (
                                                    <span className="text-xs text-gray-300 font-semibold">
                                                        Signature {index + 1} (Page {field.pageNumber || field.page || 1})
                                                    </span>
                                                )}
                                                <div className="border-2 border-dashed border-gray-400 rounded-xl bg-white overflow-hidden">
                                                    <SignatureCanvas
                                                        ref={(el) => (sigPadRefs.current[field.id] = el)}
                                                        penColor="black"
                                                        canvasProps={{
                                                            className: 'w-full h-36 cursor-crosshair'
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex justify-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => clearSinglePad(field.id)}
                                                        className="text-xs text-gray-400 hover:text-red-400 transition-colors"
                                                    >
                                                        Clear Signature
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Only render extra input boxes if the preparer placed custom 'text' or 'number' fields */}
                            {manualInputFields.map((field) => (
                                <div
                                    key={field.id}
                                    className="p-4 rounded-xl border border-gray-700 bg-[#252d3a]"
                                >
                                    <label className="block text-xs font-bold text-gray-300 uppercase mb-2">
                                        {field.label || field.type} (Page {field.pageNumber || field.page || 1})
                                    </label>
                                    <input
                                        type={field.type === 'number' ? 'number' : 'text'}
                                        placeholder={`Enter ${field.label || field.type}...`}
                                        value={customFieldValues[field.id] || ''}
                                        onChange={(e) =>
                                            setCustomFieldValues((prev) => ({
                                                ...prev,
                                                [field.id]: e.target.value
                                            }))
                                        }
                                        className="w-full p-2.5 border border-gray-600 rounded-lg text-sm bg-[#1e2530] text-white outline-none focus:border-blue-500"
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ==========================================
                        TAB 3: PRINT & UPLOAD
                       ========================================== */}
                    {activeTab === 'upload' && (
                        <div className="text-center py-6">
                            <div className="bg-[#252d3a] p-8 rounded-xl border-2 border-dashed border-gray-600 hover:border-blue-500 transition-colors space-y-5">
                                <p className="text-sm text-gray-300">
                                    Print the document, sign it with a pen, and upload a photo or scan.
                                </p>

                                {resolvedDocUrl && (
                                    <div>
                                        <button
                                            type="button"
                                            onClick={handleOpenInlineNewTab}
                                            disabled={openingBlobTab}
                                            className="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold rounded-lg border border-gray-500 transition-colors shadow-sm"
                                        >
                                            🖨️ {openingBlobTab ? 'Opening PDF...' : 'Open Printable PDF'}
                                        </button>
                                    </div>
                                )}

                                <div>
                                    <input
                                        type="file"
                                        accept=".pdf,image/*"
                                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                        className="hidden"
                                        id="signed-file-upload"
                                    />
                                    <label
                                        htmlFor="signed-file-upload"
                                        className="cursor-pointer inline-flex items-center px-5 py-2.5 bg-blue-600/20 text-blue-400 border border-blue-500/40 rounded-lg font-semibold text-sm hover:bg-blue-600/30 transition-colors"
                                    >
                                        Browse Files to Upload
                                    </label>
                                    {selectedFile && (
                                        <p className="mt-3 text-sm font-semibold text-emerald-400">
                                            Selected: {selectedFile.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="p-4 border-t border-gray-700/80 bg-[#171d26] flex justify-between items-center">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="px-5 py-2 text-sm font-semibold text-gray-300 bg-gray-700/60 hover:bg-gray-700 rounded-xl transition-colors"
                    >
                        Cancel
                    </button>

                    {activeTab !== 'read' && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="px-6 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Processing & Stamping...' : 'Submit Document'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SignatureModal;